import { BubbleSeries } from '@components-clean/core/charts/bubble/bubble-chart';
import Constants from '@utils/constants';
import { CompleteRiskFields } from '@queries/generated/fragments.generated';

export type EvaluationModelValue = { value: number; enabled: boolean };

export type EvaluationModel = {
    likelihood: EvaluationModelValue[];
    impact: EvaluationModelValue[];
    detectability: EvaluationModelValue[];
    includeDetectability: boolean;
};

const isValueAllowed = (value: number, allowedValues: EvaluationModelValue[]): boolean =>
    allowedValues.some((v) => v.enabled && v.value === value);

const isEvaluationStale = (
    evaluation: { impact?: number | null; likelihood?: number | null; detectability?: number | null },
    evaluationModel: EvaluationModel,
): boolean => {
    // matching evaluationModelId doesn't guarantee the values still fit — the model's scales can be edited after the fact
    if (!isValueAllowed(evaluation.impact ?? 0, evaluationModel.impact)) return true;
    if (!isValueAllowed(evaluation.likelihood ?? 0, evaluationModel.likelihood)) return true;
    if (
        evaluationModel.includeDetectability &&
        !isValueAllowed(evaluation.detectability ?? 0, evaluationModel.detectability)
    ) {
        return true;
    }

    return false;
};

const hasEvaluationsFromDifferentModel = (risks: CompleteRiskFields[], evaluationModel: EvaluationModel): boolean => {
    return risks.some((risk) => {
        const history = [...(risk.evaluationHistory || [])].sort(
            (a, b) => new Date(b?.updated ?? 0).getTime() - new Date(a?.updated ?? 0).getTime(),
        );
        const latest = history[0];

        return risk.status !== 'CLOSED' && !!latest && isEvaluationStale(latest, evaluationModel);
    });
};

const getModelXValues = (evaluationModel: EvaluationModel): number[] => {
    const enabledLikelihood = evaluationModel.likelihood.filter((l) => l.enabled);
    const enabledDetectability = evaluationModel.detectability.filter((d) => d.enabled);

    const computedValues = !evaluationModel.includeDetectability
        ? enabledLikelihood.map((l) => l.value)
        : enabledLikelihood.flatMap((l) => enabledDetectability.map((d) => l.value * d.value));

    return [0, ...new Set(computedValues)].sort((a, b) => a - b);
};

const getModelMaxImpact = (evaluationModel: EvaluationModel): number => {
    const enabledImpact = evaluationModel.impact.filter((i) => i.enabled);
    if (!enabledImpact.length) return 0;
    return Math.max(...enabledImpact.map((i) => i.value));
};

const mapColor = (color: string) => {
    switch (color) {
        case 'RED':
            return Constants.Colors.RED;
        case 'AMBER':
            return Constants.Colors.AMBER;
        case 'GREEN':
            return Constants.Colors.GREEN;
        default:
            return Constants.Colors.EMPTY;
    }
};

const translateRag = (rag: string | undefined, t: (key: string) => string) => {
    return t(`riskLog.riskRating.${rag}`);
};

const mapRisksToBubbleSeries = (
    risks: CompleteRiskFields[],
    t: (key: string) => string,
    includeDetectability?: boolean,
): BubbleSeries[] => {
    if (!risks?.length) return [];

    const evaluatedRisks = risks.filter((r) => (r.evaluationHistory?.length ?? 0) > 0);
    const unevaluatedRisks = risks.filter((r) => !r.evaluationHistory?.length);

    const aggregation: Record<string, { x: number; y: number; z: number; color?: string; score: number }> = {};

    evaluatedRisks.forEach((risk) => {
        const history = [...(risk.evaluationHistory || [])].sort(
            (a, b) => new Date(b?.updated ?? 0).getTime() - new Date(a?.updated ?? 0).getTime(),
        );

        const latest = history[0];

        const likelihood = latest?.likelihood ?? 0;
        const impact = latest?.impact ?? 0;
        const detectability = latest?.detectability ?? 0;

        const x = includeDetectability ? likelihood * detectability : likelihood;
        const y = impact;

        const colorKey = risk.score?.colour ?? latest?.colour ?? 'NONE';
        const score = latest?.score ?? 0;

        const key = `${x}-${y}-${colorKey}`;

        if (!aggregation[key]) {
            aggregation[key] = { x, y, z: 0, color: colorKey, score: 0 };
        }

        aggregation[key].z += 1;
        aggregation[key].score = score;
    });

    if (unevaluatedRisks.length > 0) {
        const key = '0-0-NONE';

        if (!aggregation[key]) {
            aggregation[key] = { x: 0, y: 0, z: 0, color: 'NONE', score: 0 };
        }

        aggregation[key].z += unevaluatedRisks.length;
    }

    return [
        {
            name: t('riskDashboard.riskMap.title'),
            data: Object.values(aggregation).map((item) => ({
                x: item.x,
                y: item.y,
                z: item.z,
                name: [
                    `${t('riskDashboard.riskMap.tooltip.numberOfRisks')}: ${item.z}`,
                    `${t('riskDashboard.riskMap.tooltip.rag')}: ${translateRag(item.color, t)}`,
                    `${t('riskDashboard.riskMap.tooltip.score')}: ${item.score}`,
                    `${t('riskDashboard.riskMap.tooltip.i')}: ${item.y}`,
                    `${includeDetectability ? t('riskDashboard.riskMap.tooltip.lAndD') : t('riskDashboard.riskMap.tooltip.l')}: ${item.x}`,
                ].join('<br/>'),
                color: mapColor(item.color ?? 'NONE'),
                score: item.score,
                rag: item.color ?? 'NONE',
            })),
        },
    ];
};

export { hasEvaluationsFromDifferentModel, mapRisksToBubbleSeries, getModelXValues, getModelMaxImpact };
